import type { CompanionState, Direction } from '@/lib/companion/types';

export interface CompanionFrame {
  state: CompanionState;
  direction: Direction;
  x: number;
  y: number;
  onCycleEnd?: () => void;
}

export interface FrameStore {
  get(): CompanionFrame;
  set(patch: Partial<CompanionFrame>): void;
  subscribe(fn: () => void): () => void;
}

/**
 * Mutable frame state shared between the engine and the renderer. The object
 * identity is stable so the render loop can hold one reference and read it
 * without allocating per frame; React is deliberately not involved.
 */
export function createFrameStore(initial: Partial<CompanionFrame> = {}): FrameStore {
  const frame: CompanionFrame = {
    state: 'idle',
    direction: 0,
    x: 0,
    y: 0,
    ...initial,
  };

  const listeners = new Set<() => void>();

  return {
    get: () => frame,
    set(patch) {
      Object.assign(frame, patch);
      for (const fn of listeners) fn();
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}
